import React from 'react';
import ReactDOM from 'react-dom';
import ReloadableComponentPrototype from 'ReloadableComponentPrototype';
import PersonalizedCardList from './personalized-card-list-base';
import LoggedOutCard from './logged-out-card';
import ErrorCard from './error-card';

export default class PersonalizedCardListMount extends ReloadableComponentPrototype {
  build() {
    this.cardsPlaceholder = this.node.find('.cms-personalized-card-list__cards-placeholder');
    this.loader = this.node.find('.cms-personalized-card-list__loader');

    const content = this.data.content;
    const isLoggedIn = this.data.isLoggedIn;
    const hasError = this.data.hasError;
    const loggedOutCardEnabled = this.data.loggedOutCardEnabled;

    this.eventBus.subscribe(this, 'reload_components', this.onReloadComponents);

    this.hideLoader();

    if (isLoggedIn) {
      if (hasError) {
        this.renderErrorCard(content);
      } else {
        this.renderCardList(content);
      }
    } else if (loggedOutCardEnabled) {
      this.renderLoggedOutCard(content);
    } else {
      this.renderNothing();
    }
  }

  onReloadComponents() {
    this.hideCards();
    this.showLoader();
  }

  // We don't need a showCards function because reloading the component will have the cards visible.
  // We just hide the cards for the time that it takes to get the new content.
  hideCards() {
    this.cardsPlaceholder.hide();
  }

  showLoader() {
    this.loader.removeClass('cms-personalized-card-list__loader--hidden');
  }

  hideLoader() {
    this.loader.addClass('cms-personalized-card-list__loader--hidden');
  }

  renderErrorCard(content) {
    ReactDOM.render(<ErrorCard title={content.errorCardTitle} description={content.errorCardDescription} />, this.cardsPlaceholder.get(0));
  }

  renderCardList(content) {
    const title = this.getTitle();
    const titleColor = this.data.titleColor;
    const showMore = content.showMore;
    const showLess = content.showLess;
    const cards = this.getCards();
    const narrowLayout = this.data.narrowLayout;
    const cardsLayout = this.data.cardsLayout;

    ReactDOM.render(<PersonalizedCardList title={title} titleColor={titleColor} showMore={showMore} showLess={showLess} cards={cards} narrowLayout={narrowLayout} cardsLayout={cardsLayout} />, this.cardsPlaceholder.get(0));
  }

  renderLoggedOutCard(content) {
    const title = content.loggedOutCardTitle;
    const description = content.loggedOutCardDescription;
    const login = content.loggedOutCardLogin;
    const loginPageUrl = content.loginPageUrl;
    const currentUrl = window.location.href;
    const loginLink = `${loginPageUrl}?redirect=${currentUrl}`;

    ReactDOM.render(<LoggedOutCard title={title} description={description} login={login} loginLink={loginLink} />, this.cardsPlaceholder.get(0));
  }

  renderNothing() {
    ReactDOM.render(null, this.cardsPlaceholder.get(0));
  }

  getTitle() {
    const title = this.data.content.title;

    if (title) {
      const titleWithName = title.replace('{name}', this.capitalizeIfInUpperCase(this.data.userFirstName));
      const titleWithTrimmedWhitespace = titleWithName.replace(/\s\s+/g, ' ');
      const titleWithNoSpaceBeforeComma = titleWithTrimmedWhitespace.replace(' ,', ',');
      return titleWithNoSpaceBeforeComma;
    }

    return title;
  }

  capitalizeIfInUpperCase(inputString) {
    const isInUpperCase = inputString.toUpperCase() === inputString;

    if (isInUpperCase) {
      return inputString
        .toLowerCase()
        .split(/(-|\s)/) // Splits after "-" or whitespace
        .map((part) => {
          return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join('');
    }

    return inputString;
  }

  getGeneralCardStrings() {
    const content = this.data.content;
    const title = content.generalCardTitle;
    const openInvoices = content.generalCardOpenInvoices;
    const pastDueDateInvoices = content.generalCardPastDueDateInvoices;
    const amount = content.generalCardAmount;
    const noOpenInvoices = content.generalCardNoOpenInvoices;
    const invoiceUrl = content.generalCardInvoiceUrl;

    return {
      title,
      openInvoices,
      pastDueDateInvoices,
      amount,
      noOpenInvoices,
      invoiceUrl,
    };
  }

  getMobileCardStrings() {
    const content = this.data.content;
    const title = content.mobileCardTitle;
    const subscriptionNumber = content.mobileCardSubscriptionNumber;
    const puk1 = content.mobileCardPuk1;
    const puk2 = content.mobileCardPuk2;
    const subscription = content.mobileCardSubscription;
    const balance = content.mobileCardBalance;
    const manageSubscription = content.mobileCardManageSubscription;

    return {
      title,
      subscriptionNumber,
      puk1,
      puk2,
      subscription,
      balance,
      manageSubscription,
    };
  }

  getMobileBroadbandCardStrings() {
    const content = this.data.content;
    const subscriptionNumber = content.mobileBroadbandCardSubscriptionNumber;
    const subscription = content.mobileBroadbandCardSubscription;
    const balance = content.mobileBroadbandCardBalance;
    const manageSubscription = content.mobileBroadbandCardManageSubscription;
    const troubleshoot = content.mobileBroadbandCardTroubleshoot;
    const troubleshootUrl = content.mobileBroadbandCardTroubleshootUrl;

    return {
      subscriptionNumber,
      subscription,
      balance,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getMobileHomeBroadbandCardStrings() {
    const content = this.data.content;
    const title = content.mobileHomeBroadbandCardTitle;
    const subscriptionNumber = content.mobileHomeBroadbandCardSubscriptionNumber;
    const manageSubscription = content.mobileHomeBroadbandCardManageSubscription;
    const troubleshoot = content.mobileHomeBroadbandCardTroubleshoot;
    const troubleshootUrl = content.mobileHomeBroadbandCardTroubleshootUrl;

    return {
      title,
      subscriptionNumber,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getMobileSummaryCardStrings() {
    const content = this.data.content;
    const title = content.mobileSummaryCardTitle;
    const manageSubscriptions = content.mobileSummaryCardManageSubscriptions;
    const manageSubscriptionsUrl = content.mobileSummaryCardManageSubscriptionsUrl;

    return {
      title,
      manageSubscriptions,
      manageSubscriptionsUrl,
    };
  }

  getBroadbandCardStrings() {
    const content = this.data.content;
    const title = content.broadbandCardTitle;
    const subscriptionNumber = content.broadbandCardSubscriptionNumber;
    const username = content.broadbandCardUsername;
    const manageSubscription = content.broadbandCardManageSubscription;
    const troubleshoot = content.broadbandCardTroubleshoot;
    const troubleshootUrl = content.broadbandCardTroubleshootUrl;

    return {
      title,
      subscriptionNumber,
      username,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getIptvCardStrings() {
    const content = this.data.content;
    const title = content.iptvCardTitle;
    const username = content.iptvCardUsername;
    const channelPackages = content.iptvCardChannelPackages;
    const channelPackagesUrl = content.iptvCardChannelPackagesUrl;
    const manageSubscription = content.iptvCardManageSubscription;
    const troubleshoot = content.iptvCardTroubleshoot;
    const troubleshootUrl = content.iptvCardTroubleshootUrl;

    return {
      title,
      username,
      channelPackages,
      channelPackagesUrl,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getMiptvCardStrings() {
    const content = this.data.content;
    const title = content.miptvCardTitle;
    const channelPackages = content.miptvCardChannelPackages;
    const channelPackagesUrl = content.miptvCardChannelPackagesUrl;
    const manageSubscription = content.miptvCardManageSubscription;
    const troubleshoot = content.miptvCardTroubleshoot;
    const troubleshootUrl = content.miptvCardTroubleshootUrl;

    return {
      title,
      channelPackages,
      channelPackagesUrl,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getAiptvCardStrings() {
    const content = this.data.content;
    const title = content.aiptvCardTitle;
    const channelPackages = content.aiptvCardChannelPackages;
    const channelPackagesUrl = content.aiptvCardChannelPackagesUrl;
    const manageSubscription = content.aiptvCardManageSubscription;
    const troubleshoot = content.aiptvCardTroubleshoot;
    const troubleshootUrl = content.aiptvCardTroubleshootUrl;

    return {
      title,
      channelPackages,
      channelPackagesUrl,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getTveCardStrings() {
    const content = this.data.content;
    const title = content.tveCardTitle;
    const email = content.tveCardEmail;
    const channelPackages = content.tveCardChannelPackages;
    const channelPackagesUrl = content.tveCardChannelPackagesUrl;
    const manageSubscription = content.tveCardManageSubscription;
    const troubleshoot = content.tveCardTroubleshoot;
    const troubleshootUrl = content.tveCardTroubleshootUrl;

    return {
      title,
      email,
      channelPackages,
      channelPackagesUrl,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getCableTvCardStrings() {
    const content = this.data.content;
    const title = content.cableTvCardTitle;
    const channelPackages = content.cableTvCardChannelPackages;
    const channelPackagesUrl = content.cableTvCardChannelPackagesUrl;
    const subscriptionNumber = content.cableTvCardSubscriptionNumber;
    const manageSubscription = content.cableTvCardManageSubscription;
    const troubleshoot = content.cableTvCardTroubleshoot;
    const troubleshootUrl = content.cableTvCardTroubleshootUrl;

    return {
      title,
      channelPackages,
      channelPackagesUrl,
      subscriptionNumber,
      manageSubscription,
      troubleshoot,
      troubleshootUrl,
    };
  }

  getCards() {
    const generalCardStrings = this.getGeneralCardStrings();
    const mobileCardStrings = this.getMobileCardStrings();
    const mobileBroadbandCardStrings = this.getMobileBroadbandCardStrings();
    const mobileHomeBroadbandCardStrings = this.getMobileHomeBroadbandCardStrings();
    const mobileSummaryCardStrings = this.getMobileSummaryCardStrings();
    const broadbandCardStrings = this.getBroadbandCardStrings();
    const iptvCardStrings = this.getIptvCardStrings();
    const miptvCardStrings = this.getMiptvCardStrings();
    const aiptvCardStrings = this.getAiptvCardStrings();
    const tveCardStrings = this.getTveCardStrings();
    const cableTvCardStrings = this.getCableTvCardStrings();

    const generalCards = this.data.generalCards.map((card) => ({
      ...card,
      title: generalCardStrings.title,
      type: 'general',
      strings: generalCardStrings,
    }));

    const mobileCards = this.data.mobileCards.map((card) => ({
      ...card,
      title: card.title ? this.capitalizeIfInUpperCase(card.title) : mobileCardStrings.title,
      type: 'mobile',
      strings: mobileCardStrings,
    }));

    const mobileSummaryCards = this.data.mobileSummaryCards.map((card) => ({
      ...card,
      subscriptions: this.getSubscriptionSummaries(card),
      title: mobileSummaryCardStrings.title,
      type: 'mobileSummary',
      strings: mobileSummaryCardStrings,
    }));

    const mobileBroadbandCards = this.data.mobileBroadbandCards.map((card) => ({
      ...card,
      type: 'mobileBroadband',
      strings: mobileBroadbandCardStrings,
    }));

    const mobileHomeBroadbandCards = this.data.mobileHomeBroadbandCards.map((card) => ({
      ...card,
      title: mobileHomeBroadbandCardStrings.title,
      type: 'mobileHomeBroadband',
      strings: mobileHomeBroadbandCardStrings,
    }));

    const broadbandCards = this.data.broadbandCards.map((card) => ({
      ...card,
      title: broadbandCardStrings.title,
      type: 'broadband',
      strings: broadbandCardStrings,
    }));

    const iptvCards = this.data.iptvCards.map((card) => ({
      ...card,
      title: iptvCardStrings.title,
      type: 'iptv',
      strings: iptvCardStrings,
    }));

    const miptvCards = this.data.miptvCards.map((card) => ({
      ...card,
      title: miptvCardStrings.title,
      type: 'miptv',
      strings: miptvCardStrings,
    }));

    const aiptvCards = this.data.aiptvCards.map((card) => ({
      ...card,
      title: aiptvCardStrings.title,
      type: 'aiptv',
      strings: aiptvCardStrings,
    }));

    const tveCards = this.data.tveCards.map((card) => ({
      ...card,
      title: tveCardStrings.title,
      type: 'tve',
      strings: tveCardStrings,
    }));

    const cableTvCards = this.data.cableTvCards.map((card) => ({
      ...card,
      title: cableTvCardStrings.title,
      type: 'cableTv',
      strings: cableTvCardStrings,
    }));

    return [
      ...generalCards,
      ...mobileCards,
      ...mobileSummaryCards,
      ...mobileBroadbandCards,
      ...mobileHomeBroadbandCards,
      ...broadbandCards,
      ...iptvCards,
      ...miptvCards,
      ...aiptvCards,
      ...tveCards,
      ...cableTvCards,
    ];
  }

  getSubscriptionSummaries(card) {
    if (card.subscriptions) {
      return card.subscriptions.map((subscription) => {
        const username = this.capitalizeIfInUpperCase(subscription.userName);

        return {
          ...subscription,
          text: username ? `${username}, ${subscription.phoneNumber}` : subscription.phoneNumber,
        };
      });
    }

    return [];
  }
}
